import {
  alpha,
  Avatar,
  Box,
  Container,
  LinearProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
  Tooltip,
} from '@mui/material';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import {
  emptyRows,
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  useTable,
} from 'src/components/table';
import { useCountdownDate } from 'src/hooks/use-countdown';
import { useMockedUser } from 'src/hooks/use-mocked-user';
import { useLocales } from 'src/locales';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import EllipsisCellWithTooltip from 'src/sections/clients/ellipsisCellWithTooltip';
import UserTableFiltersResult from 'src/sections/user/user-table-filters-result';
import UserTableToolbar from 'src/sections/user/user-table-toolbar';
import { useGetgiveawwayParticipatedQuery } from 'src/store/Reducer/giveaway';

const HEAD_LABEL = [
  { id: 'image', label: 'Image' },
  { id: 'title', label: 'Title' },
  { id: 'reward', label: 'Reward' },
  { id: 'startDate', label: 'Start Date' },
  { id: 'deadline', label: 'DeadLine' },
  { id: 'timeRemaining', label: 'Time Remaining' },
];
const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'won', label: 'Won' },
];
const defaultValues = {
  name: '',
  status: 'all',
};

const ParticipatedInView = () => {
  const { t } = useLocales();
  const table = useTable();
  const settings = useSettingsContext();
  const [filters, setFilters] = useState(defaultValues);
  const { state } = useLocation();
  const router = useRouter();
  const { user } = useMockedUser();

  const { dense, page, rowsPerPage, onChangeDense, onChangePage, onChangeRowsPerPage } = table;

  let { data, isLoading, isFetching } = useGetgiveawwayParticipatedQuery(
    {
      page: table.page + 1,
      limit: table.rowsPerPage,
      keyword: filters?.name,
      status: state?.won
        ? 'won'
        : state?.ongoing
        ? 'ongoing'
        : filters.status === 'all'
        ? undefined
        : filters.status,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const notFound = data?.data?.length === 0;

  const canReset = !isEqual(filters, defaultValues);

  const handleFilter = useCallback(
    (name, value) => {
      table.onResetPage();
      setFilters((previous) => {
        return { ...previous, [name]: value };
      });
    },
    [table]
  );

  const handleFilterStatus = useCallback(
    (event, newValue) => {
      handleFilter('status', newValue);
    },
    [handleFilter]
  );
  useEffect(() => {
    handleFilter(
      'status',
      (state?.won && 'won') || (state?.ongoing && 'ongoing') || filters.status
    );
    router.push(paths.dashboard.giveaway.participatedIn, {});
  }, []);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <CustomBreadcrumbs
        heading={t('home.participated_in')}
        links={[
          { name: t('home.dashboard'), href: '/dashboard' },
          { name: t('home.participated_in') },
        ]}
        sx={{ mb: 4 }}
      />
      <Box component={Paper} elevation={4}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Tabs
            value={filters.status}
            onChange={handleFilterStatus}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {STATUS_OPTIONS.map((tab) => (
              <Tab
                key={tab.value}
                iconPosition="end"
                value={tab.value}
                label={tab.label}
                icon={
                  <Label
                    variant={
                      ((tab.value === 'all' || tab.value === filters.status) && 'filled') || 'soft'
                    }
                    color={
                      (tab.value === 'won' && 'success') ||
                      (tab.value === 'ongoing' && 'warning') ||
                      (tab.value === 'lost' && 'error') ||
                      'default'
                    }
                  >
                    {tab.value === 'all' && (data?.meta?.giveawaysCount?.total || 0)}
                    {tab.value === 'ongoing' && (data?.meta?.giveawaysCount?.ongoing || 0)}
                    {tab.value === 'won' && (data?.meta?.giveawaysCount?.won || 0)}
                  </Label>
                }
              />
            ))}
          </Tabs>

          <UserTableToolbar filters={filters} onFilters={handleFilter} canReset={canReset} />
          {canReset && (
            <UserTableFiltersResult
              filters={filters}
              onFilters={handleFilter}
              onResetFilters={() => setFilters(defaultValues)}
              results={data?.meta?.totalRecords || 0}
              sx={{ p: 2.5, pt: 0 }}
            />
          )}
          <Scrollbar>
            {isLoading || isFetching ? (
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
              </Box>
            ) : (
              <Table size={dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
                <TableHeadCustom headLabel={HEAD_LABEL} />
                <TableBody>
                  {data?.data?.length > 0 ? (
                    data?.data?.map((item) => (
                      <CountdownRow
                        key={item._id}
                        id={item._id}
                        title={item?.basicInfo?.title}
                        winners={item?.winners}
                        timeRemaining={item?.dateSettings.endDate}
                        startDate={item?.dateSettings.startDate}
                        deadline={item?.dateSettings.endDate}
                        noOfCurrentParticipants={item?.participantsCount || 0}
                        reward={item?.rewardDetails?.name}
                        giveawayState={item?.status}
                        image={item?.basicInfo?.image}
                      />
                    ))
                  ) : (
                    <TableNoData notFound={notFound} />
                  )}
                  <TableEmptyRows
                    height={dense ? 30 : 40}
                    emptyRows={emptyRows(page, rowsPerPage, data?.meta?.totalRecords || 0)}
                  />
                </TableBody>
              </Table>
            )}
          </Scrollbar>
        </TableContainer>
        <TablePaginationCustom
          count={data?.meta?.totalRecords || 0}
          rowsPerPageOptions={[5, 10, 25]}
          rowsPerPage={rowsPerPage}
          page={page}
          dense={dense}
          onChangeDense={onChangeDense}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Box>
    </Container>
  );
};
export default ParticipatedInView;

const CountdownRow = ({
  id,
  title,
  reward,
  startDate,
  winners,
  deadline,
  noOfCurrentParticipants,
  timeRemaining, // this is expected to be deadline
  giveawayState,
  image,
}) => {
  const router = useRouter();
  const { user } = useMockedUser();

  // Countdown logic
  const { days, hours, minutes, seconds } = useCountdownDate(new Date(timeRemaining));

  const liveDrawDuration = 30 * 1000;
  const currentTime = new Date().getTime();
  const endTime = new Date(timeRemaining).getTime();

  // Determine status and redirect path
  let status = 'Upcoming';
  let path = null;

  if (currentTime >= endTime + liveDrawDuration || giveawayState === 'completed') {
    status = 'Result';
    path = paths.dashboard.giveaway.result(id);
  } else if (currentTime >= endTime) {
    status = 'Live Draw';
    path = paths.dashboard.giveaway.liveDraw(id);
  }

  const displayValue =
    giveawayState === 'completed'
      ? 'Result'
      : status === 'Upcoming'
      ? `${days}d ${hours}h ${minutes}m ${seconds}s`
      : status;

  const isClickable = status === 'Live Draw' || status === 'Result';

  return (
    <TableRow
      hover
      onClick={() =>
        router.push(paths.dashboard.giveaway.giveawayDetail(id), {
          state: { won: winners?.includes(user?._id) },
        })
      }
      sx={{ cursor: 'pointer' }}
    >
      <TableCell>
       <Box sx={{ display: 'flex', alignItems: 'center' }}>
         <Avatar src={image} alt={title} sx={{ width: 60, height: 60, borderRadius: 1 }} />
        {winners?.includes(user?._id) && (
          <>
            <Tooltip title={'Winner'}>
              <Iconify
                icon="material-symbols:crown-rounded"
                style={{ marginLeft: 6, color: '#f5c518' }}
              />
            </Tooltip>
          </>
        )}
       </Box>
      </TableCell>
      <EllipsisCellWithTooltip maxWidth="200px">{title}</EllipsisCellWithTooltip>
      <TableCell>{reward}</TableCell>
      <TableCell>{startDate}</TableCell>
      <TableCell>{deadline}</TableCell>

      <TableCell
        onClick={(e) => {
          e.stopPropagation();
          if (isClickable && !winners.includes(user?._id))
            router.push(path, { state: { winners } });
        }}
        sx={{
          color: isClickable ? 'primary.main' : 'text.primary',
          cursor: isClickable ? 'pointer' : 'default',
        }}
      >
        {winners.includes(user?._id) ? '-' : displayValue}
      </TableCell>
    </TableRow>
  );
};
